import { Schema } from "effect"
import { InstrumentId } from "./MarketData.ts"
import {
  AccountId,
  ClientOrderId,
  CurrencyCode,
  DenyReason,
  IsoDate,
  OrderId,
  OrderStatus,
  Product,
  RunnerId,
  StrategyRef
} from "./Money.ts"

// Domain errors that can reach API clients. Each maps to one HTTP status.

export class Unauthorized extends Schema.TaggedError<Unauthorized>()("Unauthorized", {}, { httpApiStatus: 401 }) {}

export class InvalidCredentials extends Schema.TaggedError<InvalidCredentials>()(
  "InvalidCredentials",
  {},
  { httpApiStatus: 401 }
) {}

export class TooManyLoginAttempts extends Schema.TaggedError<TooManyLoginAttempts>()(
  "TooManyLoginAttempts",
  { retryAfterSeconds: Schema.Number },
  { httpApiStatus: 429 }
) {}

export class UnknownInstrument extends Schema.TaggedError<UnknownInstrument>()(
  "UnknownInstrument",
  { instrument: Schema.String },
  { httpApiStatus: 404 }
) {}

export class NoProviderForVenue extends Schema.TaggedError<NoProviderForVenue>()(
  "NoProviderForVenue",
  { venue: Schema.String },
  { httpApiStatus: 422 }
) {}

/** The broker or data vendor needs (re)authorisation, e.g. an expired daily token. */
export class ProviderNotConnected extends Schema.TaggedError<ProviderNotConnected>()(
  "ProviderNotConnected",
  { provider: Schema.String },
  { httpApiStatus: 409 }
) {}

export class ProviderUnavailable extends Schema.TaggedError<ProviderUnavailable>()(
  "ProviderUnavailable",
  { provider: Schema.String, message: Schema.String },
  { httpApiStatus: 502 }
) {}

export class InvalidDocument extends Schema.TaggedError<InvalidDocument>()(
  "InvalidDocument",
  { message: Schema.String },
  { httpApiStatus: 400 }
) {}

// --- FX (docs/design/0005, P1) ------------------------------------------------------

/**
 * No ECB-derived rate for the pair on `date` (the fixing must be dated strictly
 * before it, and at most 7 days older). `firstAvailable` is the first date on or
 * after `date` that is covered (the day after a fixing), so the UI can offer
 * "Start at <date>"; null when no later date is covered (no data, or rates not
 * published yet).
 */
export class FxRateUnavailable extends Schema.TaggedError<FxRateUnavailable>()(
  "FxRateUnavailable",
  { from: CurrencyCode, to: CurrencyCode, date: IsoDate, firstAvailable: Schema.NullOr(IsoDate) },
  { httpApiStatus: 422 }
) {}

/** An FX series request whose `end` is before its `start`, or that spans more than `maxDays`. */
export class InvalidFxRange extends Schema.TaggedError<InvalidFxRange>()(
  "InvalidFxRange",
  { start: IsoDate, end: IsoDate, maxDays: Schema.Int },
  { httpApiStatus: 400 }
) {}

// --- Accounts and orders (P2) -------------------------------------------------------------

/** Also returned for an account owned by another user, so ids don't leak. */
export class AccountNotFound extends Schema.TaggedError<AccountNotFound>()(
  "AccountNotFound",
  { accountId: Schema.String },
  { httpApiStatus: 404 }
) {}

/** The account was reset (or archived): it is read-only. */
export class AccountArchived extends Schema.TaggedError<AccountArchived>()(
  "AccountArchived",
  { accountId: AccountId },
  { httpApiStatus: 409 }
) {}

/** The account is still running: end its session (docs/design/0008) before deleting it (0015). */
export class AccountActive extends Schema.TaggedError<AccountActive>()(
  "AccountActive",
  { accountId: AccountId },
  { httpApiStatus: 409 }
) {}

/** A well-formed request the server can't honour, e.g. a Replay start with no stored bars. */
export class InvalidAccountConfig extends Schema.TaggedError<InvalidAccountConfig>()(
  "InvalidAccountConfig",
  { message: Schema.String },
  { httpApiStatus: 422 }
) {}

/** The account's engine didn't take the command within 2 s. Safe to retry with the same clientOrderId. */
export class AccountBusy extends Schema.TaggedError<AccountBusy>()(
  "AccountBusy",
  { accountId: AccountId },
  { httpApiStatus: 503 }
) {}

/** Risk refused the order. It is journaled for audit but doesn't reserve its clientOrderId. */
export class OrderDenied extends Schema.TaggedError<OrderDenied>()(
  "OrderDenied",
  { reason: DenyReason, message: Schema.String },
  { httpApiStatus: 422 }
) {}

/** The clientOrderId already belongs to an order with a different payload. Mint a new one. */
export class ClientOrderIdConflict extends Schema.TaggedError<ClientOrderIdConflict>()(
  "ClientOrderIdConflict",
  { clientOrderId: ClientOrderId, orderId: OrderId },
  { httpApiStatus: 409 }
) {}

export class OrderNotFound extends Schema.TaggedError<OrderNotFound>()(
  "OrderNotFound",
  { orderId: Schema.String },
  { httpApiStatus: 404 }
) {}

/** The order is already terminal or a cancel is pending. */
export class OrderNotCancellable extends Schema.TaggedError<OrderNotCancellable>()(
  "OrderNotCancellable",
  { orderId: OrderId, status: OrderStatus },
  { httpApiStatus: 409 }
) {}

/** The order can't be changed (docs/design/0013): it filled or was cancelled, it's a market order or a strategy's, … */
export class OrderNotAmendable extends Schema.TaggedError<OrderNotAmendable>()(
  "OrderNotAmendable",
  { orderId: OrderId, message: Schema.String },
  { httpApiStatus: 409 }
) {}

/** No open position of that instrument and product. */
export class PositionNotFound extends Schema.TaggedError<PositionNotFound>()(
  "PositionNotFound",
  { instrumentId: Schema.String, product: Schema.String },
  { httpApiStatus: 404 }
) {}

/** Clock commands apply only to Replay accounts. */
export class NotReplayAccount extends Schema.TaggedError<NotReplayAccount>()(
  "NotReplayAccount",
  { accountId: AccountId },
  { httpApiStatus: 409 }
) {}

/** The replay reached the end of the stored bars. */
export class ReplayFinished extends Schema.TaggedError<ReplayFinished>()(
  "ReplayFinished",
  { accountId: AccountId },
  { httpApiStatus: 409 }
) {}

// --- Runners (P3) -----------------------------------------------------------------------

export class RunnerNotFound extends Schema.TaggedError<RunnerNotFound>()(
  "RunnerNotFound",
  { runnerId: Schema.String },
  { httpApiStatus: 404 }
) {}

/** One runner per (account, instrument, product). */
export class RunnerConflict extends Schema.TaggedError<RunnerConflict>()(
  "RunnerConflict",
  { runnerId: RunnerId, instrumentId: InstrumentId, product: Product },
  { httpApiStatus: 409 }
) {}

/** The command doesn't apply in the runner's current status, e.g. resuming a Stopped runner. */
export class IllegalRunnerCommand extends Schema.TaggedError<IllegalRunnerCommand>()(
  "IllegalRunnerCommand",
  { runnerId: RunnerId, command: Schema.String, status: Schema.String },
  { httpApiStatus: 409 }
) {}

/** The strategy goes short, but the product or account doesn't allow shorting. */
export class ProductDisallowsShort extends Schema.TaggedError<ProductDisallowsShort>()(
  "ProductDisallowsShort",
  { product: Product },
  { httpApiStatus: 422 }
) {}

export class InvalidRunnerParams extends Schema.TaggedError<InvalidRunnerParams>()(
  "InvalidRunnerParams",
  { message: Schema.String },
  { httpApiStatus: 422 }
) {}

export class UnknownStrategy extends Schema.TaggedError<UnknownStrategy>()(
  "UnknownStrategy",
  { strategy: Schema.String },
  { httpApiStatus: 404 }
) {}

/** Two evaluations on the same bars disagreed, so the strategy can't be paper traded. */
export class NonDeterministic extends Schema.TaggedError<NonDeterministic>()(
  "NonDeterministic",
  { strategy: StrategyRef },
  { httpApiStatus: 422 }
) {}

// --- Strategy authoring (P4) -------------------------------------------------------------

/** One compiler message, positioned for an editor marker (1-based line and column). */
export const CompileDiagnostic = Schema.Struct({
  message: Schema.String,
  line: Schema.NullOr(Schema.Int),
  column: Schema.NullOr(Schema.Int),
  severity: Schema.Literals(["error", "warning"])
})
export type CompileDiagnostic = typeof CompileDiagnostic.Type

export class StrategyNotFound extends Schema.TaggedError<StrategyNotFound>()(
  "StrategyNotFound",
  { strategyId: Schema.String, version: Schema.NullOr(Schema.Int) },
  { httpApiStatus: 404 }
) {}

/** This server's strategy library is read-only (prod runs the tested strategies of its release; docs/design/0012). */
export class ReadOnlyLibrary extends Schema.TaggedError<ReadOnlyLibrary>()(
  "ReadOnlyLibrary",
  {},
  { httpApiStatus: 403 }
) {}

/** Pushing the strategy files to GitHub failed; the commit stays local and the next push retries. */
export class PushFailed extends Schema.TaggedError<PushFailed>()(
  "PushFailed",
  { message: Schema.String },
  { httpApiStatus: 502 }
) {}

/** The strategy files couldn't be read or written. */
export class LibraryUnavailable extends Schema.TaggedError<LibraryUnavailable>()(
  "LibraryUnavailable",
  { message: Schema.String },
  { httpApiStatus: 503 }
) {}

export class StrategyCompileError extends Schema.TaggedError<StrategyCompileError>()(
  "StrategyCompileError",
  { diagnostics: Schema.Array(CompileDiagnostic) },
  { httpApiStatus: 422 }
) {}

/** Someone saved a newer version since `baseVersion`: reload `latest` before saving again. */
export class StrategyVersionConflict extends Schema.TaggedError<StrategyVersionConflict>()(
  "StrategyVersionConflict",
  { latest: Schema.Int },
  { httpApiStatus: 409 }
) {}

/** The source is over the limit (256 KB of UTF-8). */
export class SourceTooLarge extends Schema.TaggedError<SourceTooLarge>()(
  "SourceTooLarge",
  { limitBytes: Schema.Int, sizeBytes: Schema.Int },
  { httpApiStatus: 413 }
) {}

/** The declaration (untrusted UI metadata) is over 64 KB or malformed. */
export class InvalidDeclaration extends Schema.TaggedError<InvalidDeclaration>()(
  "InvalidDeclaration",
  { message: Schema.String },
  { httpApiStatus: 422 }
) {}
